/**
 * Runtime configuration loader.
 *
 * Loads config from /config.json at runtime (not baked in at build time).
 * This allows the same built assets to be deployed to DEV, QA, and PROD
 * with different configuration by swapping config.json during deploy.
 */

export interface RuntimeConfig {
  apiUrl: string;
  /**
   * Google Tag Manager container ID (e.g. "GTM-W39MB393").
   *
   * Left empty by default so GTM stays disabled unless an environment's
   * config.json explicitly sets it. This keeps the tag config-driven: an
   * environment is opted in/out by editing config.json, with no code change
   * or rebuild.
   */
  gtmId: string;
}

const DEFAULT_CONFIG: RuntimeConfig = {
  apiUrl: '',
  gtmId: '',
};

let cachedConfig: RuntimeConfig | null = null;

/**
 * Resolves the config.json URL relative to the script's own origin.
 * When embedded in Drupal, the page origin is the Drupal server, but the
 * script is served from CloudFront. Using import.meta.url ensures we fetch
 * config.json from CloudFront (where deploy.sh places it), not from Drupal.
 */
function resolveConfigUrl(): string {
  try {
    return new URL('/config.json', import.meta.url).href;
  } catch {
    // Fallback for environments where import.meta.url isn't available
    return '/config.json';
  }
}

/**
 * Loads runtime config from config.json on the script's origin.
 * Caches the result so subsequent calls return immediately.
 * Falls back to defaults if the fetch fails (e.g., in dev with Vite proxy).
 */
export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  if (cachedConfig) {
    return cachedConfig;
  }

  try {
    // Bypass the browser HTTP cache. config.json is runtime, per-environment
    // config that changes independently of the hashed bundle; a stale cached
    // copy (e.g. one fetched before a key like `gtmId` was added) would
    // otherwise be served to the app even after the CDN object is updated.
    // `no-store` forces a fresh read; the resolved config is still memoized in
    // `cachedConfig` so this fetch happens at most once per page load.
    const res = await fetch(resolveConfigUrl(), { cache: 'no-store' });
    if (res.ok) {
      const json = await res.json();
      cachedConfig = { ...DEFAULT_CONFIG, ...json };
    } else {
      cachedConfig = DEFAULT_CONFIG;
    }
  } catch {
    cachedConfig = DEFAULT_CONFIG;
  }

  return cachedConfig as RuntimeConfig;
}

/**
 * Returns the cached config synchronously.
 * Must be called AFTER loadRuntimeConfig() has resolved.
 * Throws if config hasn't been loaded yet.
 */
export function getRuntimeConfig(): RuntimeConfig {
  if (!cachedConfig) {
    throw new Error(
      'Runtime config not loaded. Call loadRuntimeConfig() before accessing config.',
    );
  }
  return cachedConfig;
}
